import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import { api } from '../../services/api';

interface DeviceTelemetryTabProps {
  deviceId: string;
}

const TIME_RANGES = [
  { label: '6h', hours: 6 },
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '48h', hours: 48 },
  { label: '7d', hours: 168 },
] as const;

function formatXAxisTick(value: string) {
  try {
    return format(new Date(value), 'HH:mm');
  } catch {
    return value;
  }
}

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '0.375rem',
  color: '#e2e8f0',
  fontSize: '0.75rem',
};

function formatTooltipLabel(label: string) {
  try {
    return format(new Date(label), 'd MMM HH:mm');
  } catch {
    return label;
  }
}

function SkeletonChart() {
  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 animate-pulse">
      <div className="h-4 w-32 bg-slate-700 rounded mb-4" />
      <div className="h-[200px] bg-slate-700/50 rounded" />
    </div>
  );
}

export default function DeviceTelemetryTab({ deviceId }: DeviceTelemetryTabProps) {
  const [hoursRange, setHoursRange] = useState(24);

  const { data: history, isLoading } = useQuery({
    queryKey: ['device-telemetry-history', deviceId, hoursRange],
    queryFn: () => api.adminGetDeviceTelemetryHistory(deviceId, hoursRange),
    refetchInterval: 60000,
  });

  const maxMemory = history?.length
    ? Math.max(...history.map((d: any) => d.memory_total_mb || 0))
    : 0;

  const maxDisk = history?.length
    ? Math.max(...history.map((d: any) => d.disk_total_mb || 0))
    : 0;

  // Detect which optional data series are available
  const hasData = useMemo(() => {
    if (!history || history.length === 0) return { battery: false, cpuTemp: false, wifiSignal: false };
    return {
      battery: history.some((d: any) => d.battery_percent != null),
      cpuTemp: history.some((d: any) => d.cpu_temp_celsius != null),
      wifiSignal: history.some((d: any) => d.wifi_signal_dbm != null),
    };
  }, [history]);

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400 mr-2">Time range:</span>
        {TIME_RANGES.map(({ label, hours }) => (
          <button
            key={hours}
            onClick={() => setHoursRange(hours)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
              hoursRange === hours
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-6">
          <SkeletonChart />
          <SkeletonChart />
          <SkeletonChart />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && (!history || history.length === 0) && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-12 text-center">
          <p className="text-slate-400 text-sm">No telemetry data available for this time range</p>
          <p className="text-slate-500 text-xs mt-1">
            Telemetry is sent every few heartbeats. Data is retained for 30 days.
          </p>
        </div>
      )}

      {/* Charts */}
      {!isLoading && history && history.length > 0 && (
        <div className="space-y-6">
          {/* CPU Usage Chart */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">CPU Usage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [`${value.toFixed(1)}%`, 'CPU']} />
                <Area type="monotone" dataKey="cpu_usage_percent" stroke="#06b6d4" strokeWidth={2} fill="url(#cpuGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* CPU Temperature Chart */}
          {hasData.cpuTemp && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-300 mb-3">CPU Temperature</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <defs>
                    <linearGradient id="tempGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}°C`} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [`${value.toFixed(1)}°C`, 'Temperature']} />
                  <ReferenceLine y={80} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: 'High', position: 'right', fill: '#ef4444', fontSize: 11 }} />
                  <Area type="monotone" dataKey="cpu_temp_celsius" stroke="#f97316" strokeWidth={2} fill="url(#tempGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Memory Usage Chart */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Memory Usage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, maxMemory || 'auto']} stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => v >= 1024 ? `${(v / 1024).toFixed(1)} GB` : `${v} MB`} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [value >= 1024 ? `${(value / 1024).toFixed(2)} GB` : `${value.toFixed(0)} MB`, 'Memory Used']} />
                {maxMemory > 0 && (
                  <ReferenceLine y={maxMemory} stroke="#a855f7" strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: 'Total', position: 'right', fill: '#a855f7', fontSize: 11 }} />
                )}
                <Area type="monotone" dataKey="memory_used_mb" stroke="#a855f7" strokeWidth={2} fill="url(#memoryGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Disk Usage Chart */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-300 mb-3">Disk Usage</h3>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="diskGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, maxDisk || 'auto']} stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => v >= 1024 ? `${(v / 1024).toFixed(1)} GB` : `${v} MB`} />
                <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [value >= 1024 ? `${(value / 1024).toFixed(2)} GB` : `${value.toFixed(0)} MB`, 'Disk Used']} />
                {maxDisk > 0 && (
                  <ReferenceLine y={maxDisk} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: 'Total', position: 'right', fill: '#22c55e', fontSize: 11 }} />
                )}
                <Area type="monotone" dataKey="disk_used_mb" stroke="#22c55e" strokeWidth={2} fill="url(#diskGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Battery Chart */}
          {hasData.battery && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-300 mb-3">Battery Level</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <defs>
                    <linearGradient id="batteryGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#eab308" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#eab308" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis domain={[0, 100]} stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [`${value.toFixed(0)}%`, 'Battery']} />
                  <ReferenceLine y={20} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.6} label={{ value: 'Low', position: 'right', fill: '#ef4444', fontSize: 11 }} />
                  <Area type="monotone" dataKey="battery_percent" stroke="#eab308" strokeWidth={2} fill="url(#batteryGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* WiFi Signal Chart */}
          {hasData.wifiSignal && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-300 mb-3">WiFi Signal Strength</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={history}>
                  <defs>
                    <linearGradient id="wifiGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="recorded_at" tickFormatter={formatXAxisTick} stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis domain={[-100, -30]} stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${v}`} />
                  <Tooltip contentStyle={tooltipStyle} labelFormatter={formatTooltipLabel} formatter={(value: number) => [`${value} dBm`, 'WiFi Signal']} />
                  <ReferenceLine y={-70} stroke="#eab308" strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: 'Fair', position: 'right', fill: '#eab308', fontSize: 11 }} />
                  <ReferenceLine y={-80} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.4} label={{ value: 'Weak', position: 'right', fill: '#ef4444', fontSize: 11 }} />
                  <Area type="monotone" dataKey="wifi_signal_dbm" stroke="#3b82f6" strokeWidth={2} fill="url(#wifiGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Data point count */}
          <p className="text-xs text-slate-500 text-right">
            {history.length} data point{history.length !== 1 ? 's' : ''} in this range
          </p>
        </div>
      )}
    </div>
  );
}
